import { issueManualLicenseBody } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useUsersContent } from '../../../content/sections/users.js';
import { useIssueManualLicense } from '../../../hooks/use-users.js';
import { userErrorMessage } from '../user-error.js';

type Kind = 'comp' | 'beta' | 'internal';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly userId: string;
}

/* Emissão manual de license (super_admin). Sem expiresAt → backend usa 90 dias. */
export function IssueManualLicenseModal({ open, onClose, userId }: Props) {
  const copy = useUsersContent();
  const [packId, setPackId] = useState('');
  const [version, setVersion] = useState('*');
  const [kind, setKind] = useState<Kind>('comp');
  const [reason, setReason] = useState('');
  const issue = useIssueManualLicense(userId);
  useEffect(() => {
    if (!open) {
      setPackId('');
      setVersion('*');
      setKind('comp');
      setReason('');
    }
  }, [open]);
  const body = { packId, version, justificationKind: kind, reason };
  const valid = issueManualLicenseBody.safeParse(body).success;
  const submit = (): void => {
    issue.mutate(body, {
      onSuccess: () => {
        toast.success(copy.issueLicenseModal.success(packId));
        onClose();
      },
      onError: (err) => {
        toast.error(userErrorMessage(err, copy.errors));
      },
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !issue.isPending) onClose();
      }}
    >
      <DialogContent locked={issue.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.issueLicenseModal.title}</DialogTitle>
          <DialogDescription>{copy.issueLicenseModal.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="im-pack">{copy.issueLicenseModal.packIdLabel}</Label>
            <Input id="im-pack" value={packId} onChange={(e) => setPackId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="im-version">{copy.issueLicenseModal.versionLabel}</Label>
            <Input id="im-version" value={version} onChange={(e) => setVersion(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="im-kind">{copy.issueLicenseModal.justificationLabel}</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
              <SelectTrigger id="im-kind" aria-label={copy.issueLicenseModal.justificationLabel}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comp">{copy.issueLicenseModal.kindComp}</SelectItem>
                <SelectItem value="beta">{copy.issueLicenseModal.kindBeta}</SelectItem>
                <SelectItem value="internal">{copy.issueLicenseModal.kindInternal}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="im-reason">{copy.issueLicenseModal.reasonLabel}</Label>
            <Textarea
              id="im-reason"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={issue.isPending}>
            {copy.issueLicenseModal.cancel}
          </Button>
          <Button loading={issue.isPending} disabled={!valid} onClick={submit}>
            {copy.issueLicenseModal.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
